import { useMemo, type ReactElement } from 'react';
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import {
  parseOccurrencesAlertKey,
  parseAlertToken,
} from '@kirin/api-client/helpers/alerts';
import {
  AlertOccurrencesModal,
  ScopedAlertsView,
  SessionView,
} from '@/components/grouped-alerts';
import { useSessionViewRoute } from '@/components/grouped-alerts/session-view/useSessionViewRoute';
import { Loading } from '@/components/shared/placeholders/Loading';
import { DataTableUserAlertsContent } from './DataTableUserAlertsTab';
import { getUserOccurrencesFiltersFromSearchParams } from './DataTableUserAlertsTab/utils';

export default function GroupAlertsTab(): ReactElement {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const splat = params['*'];
  const occurrencesAlertKey = parseOccurrencesAlertKey(splat);
  // KIR-2788: a personal-account notification click lands on the exact alert(s)
  // that fired — one opaque token (finding_key or alert-set id), resolved by the
  // backend in account scope. Same scoped landing an org user gets.
  const alertToken = parseAlertToken(splat);
  const sessionRoute = useSessionViewRoute(splat);

  const occurrencesFilters = useMemo(
    () => getUserOccurrencesFiltersFromSearchParams(searchParams),
    [searchParams]
  );

  if (sessionRoute.kind === 'loading') {
    return <Loading label="Loading..." className="py-8" />;
  }
  if (sessionRoute.kind === 'session') {
    return (
      <SessionView
        key={sessionRoute.occurrenceId}
        organizationId={null}
        occurrenceId={sessionRoute.occurrenceId}
      />
    );
  }

  if (alertToken) {
    return <ScopedAlertsView scope={{ kind: 'account' }} token={alertToken} />;
  }

  return (
    <>
      <DataTableUserAlertsContent />
      {occurrencesAlertKey && (
        <AlertOccurrencesModal
          open
          alertKey={occurrencesAlertKey}
          filters={occurrencesFilters}
          onClose={() =>
            navigate({ pathname: '/dashboard/alerts', search: location.search })
          }
        />
      )}
    </>
  );
}
